import React, { useEffect, useState } from 'react'
import { Form, Row, Col, Select, Space } from 'antd'
import { ConfirmModal } from '../modals/confirmModal'
import type { ReportStatus } from './ReportStatusFlowSettings'
import { useForm } from 'antd/lib/form/Form'
import { DropdownArrowIcon } from '../ant-overrides/icon-overrides'

interface DeleteStatusModalProps {
    status: ReportStatus
    statusIndex: number
    allStatuses: ReportStatus[]
    isModalOpen: boolean
    onCancelClicked?: () => void
    onDeleteConfirmClicked?: (remapToStatusId: number) => void
}

const NEW_STATUS_FORM_ITEM_NAME = 'new-status'
/**
 * Pops up when a user attempts to delete a report status. The main
 * function is to allow them to transfer the status to another one
 * so the reports can get updated to a status that exists.
 */
export function ReportStatusDeleteStatusModal({
    status,
    statusIndex,
    allStatuses,
    isModalOpen,
    onCancelClicked,
    onDeleteConfirmClicked,
}: DeleteStatusModalProps) {
    const [isSelectDropdownOpen, setIsSelectDropdownOpen] = useState(false)
    const [submittable, setSubmittable] = useState(false)
    const [form] = useForm()

    const values = Form.useWatch([], form)

    useEffect(() => {
        form.validateFields({ validateOnly: true })
            .then(() => setSubmittable(true))
            .catch(() => setSubmittable(false))
    }, [form, values])

    useEffect(() => {
        form.resetFields()
    }, [isModalOpen])

    const statusOptions = allStatuses
        .filter((_, i) => i !== statusIndex)
        .map((status, i) => ({
            value: i,
            label: status.name,
            className: 'new-status-option',
        }))

    return (
        <ConfirmModal
            title={window.i18n('appcore:reportStatus.deleteStatus')}
            open={isModalOpen}
            onCancel={onCancelClicked}
            isSubmitButtonDisabled={!submittable}
            onConfirmClicked={() => {
                form.validateFields()
                    .then(() => {
                        onDeleteConfirmClicked(form.getFieldValue(NEW_STATUS_FORM_ITEM_NAME))
                    })
                    .catch(() => {
                        /*form validation error */
                    })
            }}
            classNames={{ header: 'DeleteStatusModal__header' }}
            confirmButtonText={window.i18n('appcore:reportStatus.deleteStatusConfirm')}
        >
            <Form form={form} layout="vertical" requiredMark={false}>
                <Space direction="vertical" size={48} style={{ width: '100%' }}>
                    <p className="DeleteStatusModal__message">{window.i18n('appcore:reportStatus.deleteMessage')}</p>
                    <Row wrap={false} className="status-map-container">
                        <Col flex="1 0 0">
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div>
                                    <span className="status-title">
                                        {' '}
                                        {window.i18n('appcore:reportStatus.deleteStatusPreviousStatus')}
                                    </span>
                                </div>
                                <div className="previous-status-name one-row-ellipsis" style={{ lineHeight: '4rem' }}>
                                    {status.name}
                                </div>
                            </Space>
                        </Col>
                        <Col flex="1 0 0">
                            <Space direction="vertical" className="has-fullwidth">
                                <div>
                                    <span className="status-title">
                                        {' '}
                                        {window.i18n('appcore:reportStatus.deleteStatusNewStatus')}
                                    </span>
                                </div>
                                <Form.Item
                                    name={NEW_STATUS_FORM_ITEM_NAME}
                                    rules={[{ required: true }]}
                                    initialValue={null}
                                >
                                    <Select
                                        size="large"
                                        placeholder={window.i18n('appcore:reportStatus.deleteStatusSelectStatus')}
                                        className="has-fullwidth new-status-name"
                                        options={statusOptions}
                                        data-testid="report-status-transfer-select"
                                        onDropdownVisibleChange={setIsSelectDropdownOpen}
                                        suffixIcon={<DropdownArrowIcon expanded={isSelectDropdownOpen} />}
                                    />
                                </Form.Item>
                            </Space>
                        </Col>
                    </Row>
                </Space>
            </Form>
        </ConfirmModal>
    )
}
